/**
 * Shared helpers for bulk-moving tasks between projects from Cards and
 * List views. A "task" is a `(group_name, sub_group_name)` bundle that
 * may contain several usecase rows (composed / multi-objective).
 */
import { deploy_in_progress, undeploy_in_progress } from '@/shared/constants/taskStatuses';
import { displayTaskName } from './projectDeletedToast.jsx';

/**
 * A usecase cannot change project while it is deployed, or while a deploy /
 * undeploy is still running — the same lock Edit Task applies to its name.
 */
export const isUsecaseMoveLocked = (usecase) =>
    Boolean(
        usecase?.is_deployed ||
            usecase?.status === deploy_in_progress ||
            usecase?.status === undeploy_in_progress
    );

export const usecaseBundleKey = (usecase) => {
    const group = usecase?.group_name || usecase?._groupName || '';
    const sub =
        (usecase?.sub_group_name || '').trim() ||
        usecase?.usecase_name ||
        usecase?._id ||
        '';
    return `${group}::${sub}`;
};

export const taskSelectionKey = (subGroup, groupName = '') => {
    const sub =
        (subGroup?.sub_group_name || '').trim() ||
        subGroup?.usecases?.[0]?.usecase_name ||
        subGroup?.usecases?.[0]?._id ||
        '';
    return `${groupName}::${sub}`;
};

/**
 * Destination projects for the Move modal. When every selected item
 * already lives in one project, that project is omitted (Figma: other
 * projects only). Mixed-project selections keep every project so the
 * user can gather them in one place.
 */
export const getMoveDestinations = (allApps, selectedGroupNames = []) => {
    const uniqueSources = [...new Set((selectedGroupNames || []).filter(Boolean))];
    const onlySource = uniqueSources.length === 1 ? uniqueSources[0] : null;

    return (allApps || [])
        .filter((app) => app?.group_name && app.group_name !== onlySource)
        .map((app) => ({
            name: app.group_name,
            count: (app.sub_groups || []).length,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Split a selection into the usecases that may move and the ones locked by a
 * deployment. A composed task moves as one unit, so a bundle is locked when
 * ANY of its rows (looked up in `allUsecases`, falling back to the selection)
 * is deployed or has a deploy / undeploy in progress.
 */
export const splitMovableUsecases = (selectedUsecases = [], allUsecases = []) => {
    const lockedBundles = new Set();
    const markLocked = (usecase) => {
        if (isUsecaseMoveLocked(usecase)) lockedBundles.add(usecaseBundleKey(usecase));
    };
    (allUsecases || []).forEach(markLocked);
    (selectedUsecases || []).forEach(markLocked);

    const movable = [];
    const blocked = [];
    (selectedUsecases || []).forEach((usecase) => {
        (lockedBundles.has(usecaseBundleKey(usecase)) ? blocked : movable).push(usecase);
    });

    const blockedTaskNames = [];
    const seen = new Set();
    blocked.forEach((usecase) => {
        const name = displayTaskName(usecase);
        if (!seen.has(name)) {
            seen.add(name);
            blockedTaskNames.push(name);
        }
    });

    return { movable, blocked, blockedTaskNames };
};

/**
 * Collapse selected usecases into update_llm_usecase calls.
 *
 * When every sibling of a composed task is selected, one call with
 * `propagate_sub_group_name` moves the whole bundle. A partial
 * selection updates each row on its own so unselected siblings stay.
 */
export const planBulkMove = (selectedUsecases = [], allUsecases = []) => {
    const allByBundle = new Map();
    (allUsecases || []).forEach((usecase) => {
        if (!usecase?._id) return;
        const key = usecaseBundleKey(usecase);
        if (!allByBundle.has(key)) allByBundle.set(key, []);
        allByBundle.get(key).push(usecase);
    });

    const selectedByBundle = new Map();
    (selectedUsecases || []).forEach((usecase) => {
        if (!usecase?._id) return;
        const key = usecaseBundleKey(usecase);
        if (!selectedByBundle.has(key)) selectedByBundle.set(key, []);
        selectedByBundle.get(key).push(usecase);
    });

    const ops = [];
    selectedByBundle.forEach((selected) => {
        const key = usecaseBundleKey(selected[0]);
        const siblings = allByBundle.get(key) || selected;
        const selectedIds = new Set(selected.map((item) => item._id));
        const movingWholeTask =
            siblings.length > 0 && siblings.every((item) => selectedIds.has(item._id));

        if (movingWholeTask) {
            ops.push({ usecase: selected[0], propagate: true });
            return;
        }
        selected.forEach((usecase) => {
            ops.push({ usecase, propagate: false });
        });
    });
    return ops;
};

/**
 * Run the `update_llm_usecase` calls produced by `planBulkMove`.
 * Callers own toasts and refresh. `postFn` is the HTTP helper so tests
 * can inject a stub without importing the client.
 */
export const runBulkMove = async (usecases, destGroupName, allUsecases, postFn) => {
    if (!destGroupName || !Array.isArray(usecases) || usecases.length === 0) {
        return { success: false, moved: 0, failures: ['No tasks selected to move'] };
    }

    const ops = planBulkMove(usecases, allUsecases);
    const failures = [];
    let moved = 0;

    for (const op of ops) {
        // Sequential: destination uniqueness is checked per call.
        // eslint-disable-next-line no-await-in-loop
        const result = await postFn(
            'update_llm_usecase',
            buildMoveUpdatePayload(op.usecase, destGroupName, op.propagate)
        );
        if (result?.success) {
            moved += 1;
        } else {
            failures.push(result?.message || 'Failed to move task');
        }
    }

    return {
        success: failures.length === 0,
        moved,
        failures,
    };
};

/**
 * `runBulkMove` for the movable part of the selection only. Deployed tasks
 * are never sent to the server; callers report them via `blockedTaskNames`.
 * `blockedAll` is true when nothing could move because every task is locked.
 */
export const runGuardedBulkMove = async (usecases, destGroupName, allUsecases, postFn) => {
    const { movable, blocked, blockedTaskNames } = splitMovableUsecases(usecases, allUsecases);
    if (movable.length === 0) {
        const blockedAll = blocked.length > 0;
        return {
            success: false,
            moved: 0,
            failures: blockedAll ? [] : ['No tasks selected to move'],
            blockedTaskNames,
            blockedAll,
        };
    }
    const result = await runBulkMove(movable, destGroupName, allUsecases, postFn);
    return { ...result, blockedTaskNames, blockedAll: false };
};

export const buildMoveUpdatePayload = (usecase, destGroupName, propagate) => ({
    usecase_id: usecase._id,
    client_id: usecase.client_id,
    usecase_name: usecase.usecase_name,
    usecase_goal: usecase.usecase_goal ?? null,
    task_type: usecase.task_type ?? null,
    group_name: destGroupName,
    sub_group_name: (usecase.sub_group_name || '').trim() || null,
    propagate_sub_group_name: !!propagate,
    is_multilabel: usecase.is_multilabel || false,
    classify_speakers: usecase.classify_speakers || false,
    confidence_rejection_enabled: usecase.confidence_rejection_enabled !== false,
    rejection_label_name: usecase.rejection_label_name ?? null,
    task_languages: usecase.task_languages || [],
});
